import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { RELEASE_CHANNEL } from '../constants/release';

import {
  ArrowLeft,
  ChatText,
  Check,
  CircleNotch,
  House,
  PaperPlaneRight,
  Sparkle
} from '@phosphor-icons/react';
/**
 * Public Beta Feedback Page
 * Simple feedback submission for beta users
 */
const Feedback = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    feedbackType: '',
    message: '',
    email: ''
  });

  useEffect(() => {
    // Check if user is logged in
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          // Auto-fill email if logged in
          setFormData(prev => ({
            ...prev,
            email: userData.email || ''
          }));
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.feedbackType || !formData.message || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          feedback_type: formData.feedbackType,
          message: formData.message,
          email: formData.email,
          // Metadata is auto-attached by backend for logged-in users
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit feedback');
      }

      setSubmitted(true);
      toast.success('Feedback sent successfully!');
      
      // Reset form after 3 seconds and scroll to top
      setTimeout(() => {
        setFormData({
          feedbackType: '',
          message: '',
          email: user?.email || ''
        });
        setSubmitted(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const feedbackTypes = [
    { value: 'bug', label: 'Bug / Issue' },
    { value: 'feature', label: 'Feature request' },
    { value: 'improvement', label: 'Improvement idea' },
    { value: 'general', label: 'General feedback' }
  ];

  return (
    <div className="min-h-screen bg-[#161B26] text-white flex flex-col">
      <SEO 
        title="Feedback - Man & Machine"
        description="Help shape Man & Machine. Share your Public Beta feedback."
      />
      
      

      <div className="flex-1 py-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Back to House Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              <span>Back to House</span>
            </button>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                <Sparkle size={24} className="text-white" />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Help Shape Man & Machine
            </h1>
            <p className="text-lg text-[#A0AEC0]">
              You're using the {RELEASE_CHANNEL}. Your feedback directly influences what we build next.
            </p>
          </motion.div>

          {/* Success State */}
          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 p-6 rounded-2xl bg-green-500/10 border border-green-500/30"
            >
              <div className="flex items-center gap-3">
                <Check size={24} className="text-green-400" />
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    Thanks for your feedback!
                  </h3>
                  <p className="text-[#A0AEC0] text-sm">
                    It's been sent to the Man & Machine team.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Feedback Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#1A1B23] border border-[#2D2E3A] rounded-2xl p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Feedback Type */}
              <div>
                <label htmlFor="feedbackType" className="block text-sm font-medium text-white mb-2">
                  Feedback type <span className="text-red-400">*</span>
                </label>
                <select
                  id="feedbackType"
                  name="feedbackType"
                  value={formData.feedbackType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#161B26] border border-[#2D2E3A] rounded-lg text-white focus:outline-none focus:border-[#6366F1] transition-colors"
                >
                  <option value="">Select a type...</option>
                  {feedbackTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ChatText */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Your feedback <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Tell us what you think..."
                  className="w-full px-4 py-3 bg-[#161B26] border border-[#2D2E3A] rounded-lg text-white placeholder-[#718096] focus:outline-none focus:border-[#6366F1] transition-colors resize-none"
                />
                <p className="mt-2 text-xs text-[#718096]">
                  Be as specific as possible. Include steps to reproduce if reporting a bug.
                </p>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email address <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-[#161B26] border border-[#2D2E3A] rounded-lg text-white placeholder-[#718096] focus:outline-none focus:border-[#6366F1] transition-colors"
                />
                {user && (
                  <p className="mt-2 text-xs text-[#718096]">
                    You can edit this if you prefer to use a different email.
                  </p>
                )}
              </div>

              {/* Auto-attached metadata info */}
              {user && (
                <div className="p-4 rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20">
                  <p className="text-xs text-[#A0AEC0]">
                    <strong className="text-white">Auto-attached:</strong> Your User ID, Organisation ID, and plan tier will be included to help us assist you faster.
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || submitted}
                className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : submitted ? (
                  <>
                    <Check size={20} />
                    Sent!
                  </>
                ) : (
                  <>
                    <PaperPlaneRight size={20} />
                    Send feedback
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-6 text-center"
          >
            <p className="text-sm text-[#718096]">
              Need immediate support?{' '}
              <button
                onClick={() => navigate('/contact')}
                className="text-[#6366F1] hover:text-[#EC4899] underline transition-colors"
              >
                Contact us
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
